import { BookIcon, BotIcon, BriefcaseIcon, Building2Icon, CameraIcon, CircleUserIcon, CodeIcon, DollarSignIcon, FlaskConicalIcon, FolderIcon, GlobeIcon, ImageIcon, LightbulbIcon, PackageIcon, PaletteIcon, PencilIcon, PlugIcon, RocketIcon, SearchIcon, ShirtIcon, ShoppingCartIcon, SmartphoneIcon, SparkleIcon, StoreIcon, TagIcon, TrendingUpIcon, UserIcon, UsersIcon, WrenchIcon, ZapIcon } from "lucide-react";
/**
 * Onboarding Configuration
 *
 * Two distinct paths from the start:
 *   Platform  — Non-technical: brands, D2C, agencies, creators using the web app
 *   Technical — Developers integrating via API for apps, marketplaces, fashion tech
 *
 * Platform path: Path → Use Case → Your Role → Company → Volume (if brand) → Goals
 * Technical path: Path → Your Role → Company → API Usage → Goals
 *
 * Icons: lucide-react components. See https://lucide.dev/icons/
 * Names are case-sensitive (e.g. ShirtIcon not PiTshirt).
 */

export const PATH_TYPES = {
  PLATFORM: "platform",
  TECHNICAL: "technical",
};

export const USER_TYPES = {
  FASHION_BRAND: "fashion_brand",
  D2C_BRAND: "d2c_brand",
  ECOMMERCE_MARKETPLACE: "ecommerce_marketplace",
  APP_DEVELOPER: "app_developer",
  FASHION_TECH: "fashion_tech",
  AGENCY: "agency",
  INDIVIDUAL: "individual",
  OTHER: "other",
};

export const USER_TYPE_LABELS = {
  [USER_TYPES.FASHION_BRAND]: "Fashion Brand",
  [USER_TYPES.D2C_BRAND]: "D2C Brand",
  [USER_TYPES.ECOMMERCE_MARKETPLACE]: "E-Commerce Marketplace",
  [USER_TYPES.APP_DEVELOPER]: "App Developer",
  [USER_TYPES.FASHION_TECH]: "Fashion Tech Company",
  [USER_TYPES.AGENCY]: "Agency",
  [USER_TYPES.INDIVIDUAL]: "Individual / Creator",
  [USER_TYPES.OTHER]: "Other",
};

const PLATFORM_TYPES = [
  USER_TYPES.FASHION_BRAND,
  USER_TYPES.D2C_BRAND,
  USER_TYPES.AGENCY,
  USER_TYPES.INDIVIDUAL,
];

const DEVELOPER_TYPES = [
  USER_TYPES.APP_DEVELOPER,
  USER_TYPES.FASHION_TECH,
  USER_TYPES.ECOMMERCE_MARKETPLACE,
];

/** User type options for API/developer path */
const USER_TYPE_OPTIONS_API = [
  { value: USER_TYPES.APP_DEVELOPER, label: USER_TYPE_LABELS[USER_TYPES.APP_DEVELOPER], icon: CodeIcon, description: "Building apps or integrating via API" },
  { value: USER_TYPES.FASHION_TECH, label: USER_TYPE_LABELS[USER_TYPES.FASHION_TECH], icon: RocketIcon, description: "SaaS or tech platforms for fashion" },
  { value: USER_TYPES.ECOMMERCE_MARKETPLACE, label: USER_TYPE_LABELS[USER_TYPES.ECOMMERCE_MARKETPLACE], icon: ShoppingCartIcon, description: "Online marketplaces & multi-brand stores" },
  { value: USER_TYPES.OTHER, label: "Other", icon: LightbulbIcon },
];

/** User type options for platform/creative path */
const USER_TYPE_OPTIONS_PLATFORM = [
  { value: USER_TYPES.FASHION_BRAND, label: USER_TYPE_LABELS[USER_TYPES.FASHION_BRAND], icon: ShirtIcon, description: "Clothing companies & fashion houses" },
  { value: USER_TYPES.D2C_BRAND, label: USER_TYPE_LABELS[USER_TYPES.D2C_BRAND], icon: StoreIcon, description: "Direct-to-consumer brands selling online" },
  { value: USER_TYPES.AGENCY, label: USER_TYPE_LABELS[USER_TYPES.AGENCY], icon: Building2Icon, description: "Creative, marketing, or e-commerce agencies" },
  { value: USER_TYPES.INDIVIDUAL, label: USER_TYPE_LABELS[USER_TYPES.INDIVIDUAL], icon: CircleUserIcon, description: "Freelancers, designers, content creators" },
  { value: USER_TYPES.OTHER, label: "Other", icon: LightbulbIcon },
];

const BRAND_TYPES = [
  USER_TYPES.FASHION_BRAND,
  USER_TYPES.D2C_BRAND,
];

const ORG_TYPES = [
  USER_TYPES.FASHION_BRAND,
  USER_TYPES.D2C_BRAND,
  USER_TYPES.ECOMMERCE_MARKETPLACE,
  USER_TYPES.FASHION_TECH,
  USER_TYPES.AGENCY,
];

/**
 * Role-specific goal options.
 * Each user type maps to goals that are relevant to their workflow.
 */
const GOALS_BY_TYPE = {
  [USER_TYPES.FASHION_BRAND]: [
    { value: "reduce_photo_costs", label: "Reduce photography & model costs", icon: DollarSignIcon },
    { value: "speed_up_launches", label: "Speed up product launches", icon: ZapIcon },
    { value: "scale_catalog", label: "Scale catalog imagery", icon: TrendingUpIcon },
    { value: "improve_quality", label: "Improve visual quality & consistency", icon: SparkleIcon },
    { value: "diverse_models", label: "Show products on diverse models", icon: GlobeIcon },
  ],
  [USER_TYPES.D2C_BRAND]: [
    { value: "reduce_photo_costs", label: "Reduce photography & model costs", icon: DollarSignIcon },
    { value: "speed_up_launches", label: "Speed up product launches", icon: ZapIcon },
    { value: "improve_conversion", label: "Improve online conversion rates", icon: ShoppingCartIcon },
    { value: "scale_catalog", label: "Scale catalog imagery", icon: TrendingUpIcon },
    { value: "social_content", label: "Create social media content faster", icon: SmartphoneIcon },
  ],
  [USER_TYPES.ECOMMERCE_MARKETPLACE]: [
    { value: "reduce_returns", label: "Reduce returns with better fit visualization", icon: PackageIcon },
    { value: "improve_conversion", label: "Improve buyer conversion rates", icon: ShoppingCartIcon },
    { value: "standardize_imagery", label: "Standardize seller product imagery", icon: ImageIcon },
    { value: "scale_tryon", label: "Offer virtual try-on to millions of SKUs", icon: TrendingUpIcon },
    { value: "enhance_experience", label: "Enhance buyer shopping experience", icon: SparkleIcon },
  ],
  [USER_TYPES.APP_DEVELOPER]: [
    { value: "integrate_tryon_api", label: "Integrate virtual try-on via API", icon: PlugIcon },
    { value: "build_features", label: "Build fashion AI features into my app", icon: WrenchIcon },
    { value: "reduce_dev_time", label: "Reduce development time with pre-built models", icon: ZapIcon },
    { value: "access_models", label: "Access state-of-the-art AI models", icon: BotIcon },
    { value: "prototype_fast", label: "Prototype and test ideas quickly", icon: FlaskConicalIcon },
  ],
  [USER_TYPES.FASHION_TECH]: [
    { value: "integrate_tryon_api", label: "Add virtual try-on to our platform", icon: PlugIcon },
    { value: "white_label", label: "White-label AI features for our clients", icon: TagIcon },
    { value: "reduce_dev_time", label: "Reduce R&D time with pre-built models", icon: ZapIcon },
    { value: "scale_ai_features", label: "Scale AI features across our product", icon: TrendingUpIcon },
    { value: "competitive_edge", label: "Gain a competitive edge with AI", icon: RocketIcon },
  ],
  [USER_TYPES.AGENCY]: [
    { value: "serve_more_clients", label: "Serve more clients with less overhead", icon: UsersIcon },
    { value: "faster_turnaround", label: "Reduce turnaround time on deliverables", icon: ZapIcon },
    { value: "improve_quality", label: "Improve creative output quality", icon: SparkleIcon },
    { value: "scale_production", label: "Scale content production", icon: TrendingUpIcon },
    { value: "new_services", label: "Offer new AI-powered services", icon: LightbulbIcon },
  ],
  [USER_TYPES.INDIVIDUAL]: [
    { value: "create_content", label: "Create fashion content for social media", icon: SmartphoneIcon },
    { value: "experiment_styles", label: "Experiment with outfit ideas & styles", icon: FlaskConicalIcon },
    { value: "build_portfolio", label: "Build a creative portfolio", icon: FolderIcon },
    { value: "learn_ai", label: "Learn and explore AI tools", icon: BotIcon },
    { value: "freelance_work", label: "Use for freelance / client work", icon: BriefcaseIcon },
  ],
  [USER_TYPES.OTHER]: [
    { value: "explore", label: "Explore what TryOn AI can do", icon: FlaskConicalIcon },
    { value: "evaluate", label: "Evaluate for my organization", icon: SearchIcon },
    { value: "research", label: "Research / academic use", icon: BookIcon },
    { value: "personal", label: "Personal use", icon: CircleUserIcon },
  ],
};

/**
 * Onboarding steps/questions configuration.
 *
 * applicableTo = "pre_type"      → shown before user type is known
 * applicableTo = [USER_TYPES...] → shown only for those user types
 * applicableTo = "all"           → shown for every user type (resolved dynamically for GOALS)
 */
export const ONBOARDING_STEPS = {
  PATH: {
    id: "path",
    question: "What are you here for?",
    subtitle: "Choose your path",
    type: "select",
    options: [
      {
        value: PATH_TYPES.PLATFORM,
        label: "Use the platform",
        icon: PaletteIcon,
        description: "Create designs, try-on, catalogs — no coding required",
      },
      {
        value: PATH_TYPES.TECHNICAL,
        label: "Integrate via API",
        icon: CodeIcon,
        description: "Build apps, integrate into your product",
      },
    ],
    required: true,
    applicableTo: "pre_type",
  },

  USE_CASE: {
    id: "use_case",
    question: "What do you plan to use TryOn AI for?",
    subtitle: "Select all that apply",
    type: "multi-select",
    options: [
      { value: "virtual_tryon", label: "Virtual Try-On", icon: ShirtIcon, description: "See how clothes look on models or customers" },
      { value: "model_generation", label: "AI Model Generation", icon: UserIcon, description: "Generate diverse fashion models" },
      { value: "cataloging", label: "Product Cataloging", icon: CameraIcon, description: "Create consistent product imagery at scale" },
      { value: "garment_generation", label: "Garment Design", icon: PencilIcon, description: "Generate or edit garment designs" },
      { value: "image_editing", label: "Image Editing", icon: ImageIcon, description: "Enhance, relight, or edit fashion photos" },
      { value: "other", label: "Something Else", icon: LightbulbIcon },
    ],
    required: true,
    applicableTo: "pre_type",
  },

  USER_TYPE: {
    id: "user_type",
    question: "What best describes you?",
    subtitle: "This helps us tailor your experience",
    type: "select",
    options: [], // Resolved dynamically via getUserTypeOptionsForUseCase
    required: true,
    applicableTo: "pre_type",
  },

  COMPANY_NAME: {
    id: "company_name",
    question: "What's your company or brand name?",
    type: "text",
    placeholder: "Enter your company/brand name",
    required: true,
    applicableTo: ORG_TYPES,
  },

  MONTHLY_VOLUME: {
    id: "monthly_volume",
    question: "How many products do you typically work with per month?",
    subtitle: "Helps us recommend the right plan for you",
    type: "select",
    options: [
      { value: "1-10", label: "1-10 products" },
      { value: "11-50", label: "11-50 products" },
      { value: "51-100", label: "51-100 products" },
      { value: "100+", label: "100+ products" },
    ],
    required: false,
    applicableTo: BRAND_TYPES,
  },

  API_REQUEST_VOLUME: {
    id: "api_request_volume",
    question: "How many API requests are you looking for?",
    subtitle: "Helps us understand the scale of your needs",
    type: "select",
    options: [
      { value: "1k-10k", label: "1K – 10K requests/month" },
      { value: "10k-50k", label: "10K – 50K requests/month" },
      { value: "50k-100k", label: "50K – 100K requests/month" },
      { value: "100k-500k", label: "100K – 500K requests/month" },
      { value: "500k+", label: "500K+ requests/month" },
    ],
    required: false,
    applicableTo: DEVELOPER_TYPES,
  },

  API_USAGE: {
    id: "api_usage",
    question: "How do you plan to use our API?",
    subtitle: "Helps us set up the right access for you",
    type: "select",
    options: [
      { value: "prototyping", label: "Prototyping / exploring", icon: FlaskConicalIcon },
      { value: "integrating", label: "Integrating into an existing app", icon: PlugIcon },
      { value: "building_new", label: "Building a new product", icon: WrenchIcon },
      { value: "high_volume", label: "High-volume production use", icon: TrendingUpIcon },
    ],
    required: false,
    applicableTo: DEVELOPER_TYPES,
  },

  GOALS: {
    id: "goals",
    question: "What are your main goals?",
    subtitle: "Select all that apply — we'll personalize your dashboard",
    type: "multi-select",
    options: [],  // Resolved dynamically via getGoalsForUserType
    required: false,
    applicableTo: "all",
  },
};

/**
 * Get the goal options for a specific user type.
 */
export const getGoalsForUserType = (userType) => {
  return GOALS_BY_TYPE[userType] || GOALS_BY_TYPE[USER_TYPES.OTHER];
};

/**
 * Get user type options based on path (technical vs platform).
 */
export const getUserTypeOptionsForPath = (path) => {
  return path === PATH_TYPES.TECHNICAL ? USER_TYPE_OPTIONS_API : USER_TYPE_OPTIONS_PLATFORM;
};

/**
 * Resolve a step with dynamic options based on form data.
 */
export const resolveStepOptions = (step, formData) => {
  if (step.id === "user_type") {
    return { ...step, options: getUserTypeOptionsForPath(formData.path) };
  }
  if (step.id === "goals" && formData.user_type) {
    return { ...step, options: getGoalsForUserType(formData.user_type) };
  }
  return step;
};

/**
 * Steps when path is not yet selected.
 */
export const getInitialSteps = () => {
  return [ONBOARDING_STEPS.PATH];
};

/**
 * Full platform step list (all 6 steps) so the progress indicator always shows the full journey.
 * Applicability (monthly_volume for brands, company_name for orgs) is handled when navigating.
 */
const getPlatformSteps = (userType) => {
  const goalsStep = { ...ONBOARDING_STEPS.GOALS, options: getGoalsForUserType(userType) };
  return [
    ONBOARDING_STEPS.PATH,
    ONBOARDING_STEPS.USE_CASE,
    ONBOARDING_STEPS.USER_TYPE,
    ONBOARDING_STEPS.MONTHLY_VOLUME,
    ONBOARDING_STEPS.COMPANY_NAME,
    goalsStep,
  ];
};

/**
 * Whether a step should be shown given current path and user type (used to skip inapplicable steps when navigating).
 */
export const isStepApplicable = (step, path, userType) => {
  if (!step || !step.applicableTo) return true;
  if (step.applicableTo === "pre_type" || step.applicableTo === "all") return true;
  if (Array.isArray(step.applicableTo)) return userType != null && step.applicableTo.includes(userType);
  return true;
};

/**
 * Get steps for technical path: Path, User Type, API Request Volume, API Usage, Company name, Goals.
 */
const getTechnicalSteps = (userType) => {
  const steps = [
    ONBOARDING_STEPS.PATH,
    ONBOARDING_STEPS.USER_TYPE,
    ONBOARDING_STEPS.API_REQUEST_VOLUME,
    ONBOARDING_STEPS.API_USAGE,
    ONBOARDING_STEPS.COMPANY_NAME,
    ONBOARDING_STEPS.GOALS,
  ];
  return steps.map((step) =>
    step.id === "goals" ? { ...step, options: getGoalsForUserType(userType) } : step
  );
};

/**
 * Get the full step sequence for a given path and user type.
 */
export const getStepsForPathAndUserType = (path, userType) => {
  if (!path) return getInitialSteps();
  if (path === PATH_TYPES.TECHNICAL) return getTechnicalSteps(userType);
  return getPlatformSteps(userType);
};

/**
 * Maximum step count across all paths (for progress bar before path/userType known).
 */
export const getMaxStepCount = () => {
  const platformMax = Math.max(
    ...Object.values(USER_TYPES).map((t) => getPlatformSteps(t).length)
  );
  const technicalSteps = getTechnicalSteps(USER_TYPES.APP_DEVELOPER).length;
  return Math.max(platformMax, technicalSteps);
};

/** @deprecated Use getStepsForPathAndUserType */
export const getStepsForUserType = (userType) => {
  return getPlatformSteps(userType);
};

export const getStepOrder = (userType) => {
  const steps = getPlatformSteps(userType);
  return steps.map((step) => step.id);
};

